import { useCallback, useSyncExternalStore } from "react";

/**
 * A value a read-only session (a super admin viewing as someone) holds for
 * the rest of the page instead of saving it, shared by every instance of the
 * hook: closing the Maxwell rail on one surface, or hiding one setup
 * checklist, reaches every other surface showing it. Keyed by the viewed
 * user; a reload clears it.
 */
export function createPageOverride<T>() {
  const values = new Map<string, T>();
  const listeners = new Set<() => void>();
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  return function usePageOverride(key: string | undefined): [T | undefined, (value: T) => void] {
    const value = useSyncExternalStore(
      subscribe,
      () => (key ? values.get(key) : undefined),
      () => undefined,
    );
    const set = useCallback(
      (next: T) => {
        if (!key) return;
        values.set(key, next);
        for (const listener of listeners) listener();
      },
      [key],
    );
    return [value, set];
  };
}
